import React from "react";
import Avatar from "components/avatar";
import Typography from "components/typography";
import { useAppTranslation } from "locale/useAppTranslation";
import { TransportResourceGetVendorDetailRes } from "_services/modules/transport-resource/vendor";

type VendorDetail = TransportResourceGetVendorDetailRes & {
    vendor_logo?: string;
};

interface VendorDetailCardProps {
    detail: VendorDetail;
}

interface InfoFieldProps {
    label: string;
    value?: string | null;
}

const InfoField: React.FC<InfoFieldProps> = ({ label, value }) => (
    <div className="flex flex-col gap-1">
        <Typography variant="bodySmall" tone="secondary" className="font-medium text-greyScale-60">
            {label}
        </Typography>
        <Typography variant="body" className="font-semibold text-text-primary">
            {value || "—"}
        </Typography>
    </div>
);

const VendorDetailCard: React.FC<VendorDetailCardProps> = ({ detail }) => {
    const { t } = useAppTranslation("vendor");

    const isActive = detail.status?.toLowerCase() === "active";

    // Top-level vendor contact information (with primary branch fallback if needed)
    const primaryBranch = detail.branches?.[0];
    const vendorEmail = detail.email || primaryBranch?.pic_email;
    const contactPersonName = detail.contact_person_name || primaryBranch?.pic_name;
    const contactPersonPhone = detail.contact_person_phone_number || primaryBranch?.pic_phone;

    const vendorLogo = detail.vendor_logo;

    return (
        <section className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
            {/* Top Row: Avatar + Name + Subtitle + Status */}
            <div className="flex items-center justify-between p-6">
                <div className="flex items-center gap-4">
                    <Avatar size="xl" name={detail.vendor_name} {...(vendorLogo ? { src: vendorLogo } : {})} />
                    <div className="flex flex-col gap-1">
                        <Typography variant="heading4" className="font-bold text-text-primary">
                            {detail.vendor_name || "—"}
                        </Typography>
                        <div className="flex items-center gap-1.5 text-sm text-greyScale-60">
                            <span className="capitalize">{detail.vendor_type || "Company"}</span>
                            <span>•</span>
                            <span>{detail.vendor_code || "—"}</span>
                        </div>
                    </div>
                </div>

                <div
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold capitalize ${
                        isActive
                            ? "bg-primary-50 text-primary-600"
                            : "bg-greyScale-90 text-greyScale-60"
                    }`}
                >
                    {detail.status || "—"}
                </div>
            </div>

            {/* Dotted Divider Line */}
            <div className="px-6">
                <div className="border-t border-dashed border-border" />
            </div>

            {/* Main Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 p-6">
                <InfoField
                    label={t("label-legal-name") || "Legal Name"}
                    value={detail.legal_name}
                />
                <InfoField
                    label={t("label-vendor-email") || "Vendor Email"}
                    value={vendorEmail}
                />
                <InfoField
                    label={t("label-contact-person-name") || "Contact Person Name"}
                    value={contactPersonName}
                />
                <InfoField
                    label={t("label-contact-person-phone") || "Contact Person Phone Number"}
                    value={contactPersonPhone}
                />
                <InfoField
                    label={t("label-npwp") || "NPWP"}
                    value={detail.npwp}
                />
            </div>
        </section>
    );
};

export default VendorDetailCard;
